import React, { createContext, useContext, useEffect, useReducer } from 'react';
import { IAiAssistantAction, IAiAssistantState, IChatMessage, IContentBlock } from './types';

const FAV_STORAGE_KEY = 'cm_ai_assistant_fav_sessions';

function loadFavoriteSessions(): Record<number, number[]> {
  try {
    const raw = localStorage.getItem(FAV_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const result: Record<number, number[]> = {};
    for (const [key, val] of Object.entries(parsed)) {
      const numKey = Number(key);
      if (!isNaN(numKey) && Array.isArray(val) && (val as any[]).every((v) => typeof v === 'number')) {
        result[numKey] = val as number[];
      }
    }
    return result;
  } catch {
    return {};
  }
}

function saveFavoriteSessions(favorites: Record<number, number[]>): void {
  try {
    const clean: Record<number, number[]> = {};
    for (const [k, v] of Object.entries(favorites)) {
      if ((v as number[]).length > 0) clean[Number(k)] = v as number[];
    }
    localStorage.setItem(FAV_STORAGE_KEY, JSON.stringify(clean));
  } catch {
    // Silently handle storage errors
  }
}

const initialState: IAiAssistantState = {
  projects: [],
  selectedProjectId: null,
  sessions: [],
  tabs: [],
  activeTabId: null,
  fileTree: [],
  messagesBySession: {},
  streamingSessionId: null,
  fileContents: {},
  loadingProjects: false,
  loadingSessions: false,
  loadingFiles: false,
  agentFileChangeSeq: 0,
  unreadSessionIds: [],
  favoriteSessionsByProject: {},
};

const asArray = <T,>(value: T[] | unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

function reducer(state: IAiAssistantState, action: IAiAssistantAction): IAiAssistantState {
  switch (action.type) {
    // services/request get() devuelve `{} as T` ante CUALQUIER error (502,
    // timeout, sesión caducada): sin normalizar aquí, un fetch fallido guarda
    // un objeto donde va una lista y el siguiente .filter/.map tumba la app
    // («V.sessions.filter is not a function», incidente esti 4 ago 2026).
    case 'SET_PROJECTS':
      return { ...state, projects: asArray(action.projects), loadingProjects: false };

    case 'ADD_PROJECT':
      return { ...state, projects: [...state.projects, action.project] };

    case 'UPDATE_PROJECT':
      return {
        ...state,
        projects: state.projects.map((p) => (p.id === action.project.id ? action.project : p)),
      };

    case 'DELETE_PROJECT': {
      const newTabs = state.tabs.filter((t) => {
        if (t.type === 'session') {
          const session = state.sessions.find((s) => s.id === t.sessionId);
          return !session || session.projectId !== action.projectId;
        }
        return true;
      });
      const { [action.projectId]: _, ...remainingFavs } = state.favoriteSessionsByProject;
      saveFavoriteSessions(remainingFavs);
      return {
        ...state,
        projects: state.projects.filter((p) => p.id !== action.projectId),
        selectedProjectId: state.selectedProjectId === action.projectId ? null : state.selectedProjectId,
        sessions: state.sessions.filter((s) => s.projectId !== action.projectId),
        tabs: newTabs,
        activeTabId: newTabs.find((t) => t.id === state.activeTabId) ? state.activeTabId : newTabs[0]?.id || null,
        favoriteSessionsByProject: remainingFavs,
      };
    }

    case 'SELECT_PROJECT':
      return { ...state, selectedProjectId: action.projectId };

    case 'SET_SESSIONS':
      return { ...state, sessions: asArray(action.sessions), loadingSessions: false };

    case 'ADD_SESSION':
      return { ...state, sessions: [...state.sessions, action.session] };

    case 'UPDATE_SESSION':
      return {
        ...state,
        sessions: state.sessions.map((s) => (s.id === action.session.id ? action.session : s)),
        tabs: state.tabs.map((t) =>
          t.type === 'session' && t.sessionId === action.session.id
            ? { ...t, label: action.session.title }
            : t,
        ),
      };

    case 'DELETE_SESSION': {
      const newTabs = state.tabs.filter(
        (t) => !(t.type === 'session' && t.sessionId === action.sessionId),
      );
      const newMessages = { ...state.messagesBySession };
      delete newMessages[action.sessionId];
      const deletedSession = state.sessions.find((s) => s.id === action.sessionId);
      let newFavs = state.favoriteSessionsByProject;
      if (deletedSession) {
        const projFavs = newFavs[deletedSession.projectId];
        if (projFavs?.includes(action.sessionId)) {
          const filtered = projFavs.filter((id) => id !== action.sessionId);
          newFavs = { ...newFavs, [deletedSession.projectId]: filtered };
          if (filtered.length === 0) delete newFavs[deletedSession.projectId];
          saveFavoriteSessions(newFavs);
        }
      }
      return {
        ...state,
        sessions: state.sessions.filter((s) => s.id !== action.sessionId),
        tabs: newTabs,
        activeTabId: newTabs.find((t) => t.id === state.activeTabId)
          ? state.activeTabId
          : newTabs[0]?.id || null,
        messagesBySession: newMessages,
        favoriteSessionsByProject: newFavs,
      };
    }

    case 'OPEN_TAB': {
      const existing = state.tabs.find((t) => t.id === action.tab.id);
      const unread = action.tab.sessionId
        ? state.unreadSessionIds.filter((id) => id !== action.tab.sessionId)
        : state.unreadSessionIds;
      if (existing) {
        return { ...state, activeTabId: action.tab.id, unreadSessionIds: unread };
      }
      return { ...state, tabs: [...state.tabs, action.tab], activeTabId: action.tab.id, unreadSessionIds: unread };
    }

    case 'CLOSE_TAB': {
      const closedTab = state.tabs.find((t) => t.id === action.tabId);
      const newTabs = state.tabs.filter((t) => t.id !== action.tabId);
      let newActiveId = state.activeTabId;
      if (state.activeTabId === action.tabId) {
        const closedIndex = state.tabs.findIndex((t) => t.id === action.tabId);
        const nextTab = newTabs[Math.min(closedIndex, newTabs.length - 1)];
        newActiveId = nextTab?.id || null;
      }
      // Drop the file's cache on close so reopening always fetches fresh content —
      // otherwise an agent edit followed by close+reopen would show stale content.
      let newFileContents = state.fileContents;
      if (closedTab?.type === 'file' && closedTab.filePath && closedTab.filePath in state.fileContents) {
        const { [closedTab.filePath]: _omit, ...rest } = state.fileContents;
        newFileContents = rest;
      }
      return { ...state, tabs: newTabs, activeTabId: newActiveId, fileContents: newFileContents };
    }

    case 'SET_ACTIVE_TAB': {
      const tab = state.tabs.find((t) => t.id === action.tabId);
      const unread = tab?.sessionId
        ? state.unreadSessionIds.filter((id) => id !== tab.sessionId)
        : state.unreadSessionIds;
      return { ...state, activeTabId: action.tabId, unreadSessionIds: unread };
    }

    case 'APPEND_MESSAGE': {
      const msgs = state.messagesBySession[action.sessionId] || [];
      const tabId = `session:${action.sessionId}`;
      const shouldMarkUnread =
        action.message.role === 'assistant'
        && state.activeTabId !== tabId
        && !state.unreadSessionIds.includes(action.sessionId);
      return {
        ...state,
        messagesBySession: {
          ...state.messagesBySession,
          [action.sessionId]: [...msgs, action.message],
        },
        unreadSessionIds: shouldMarkUnread
          ? [...state.unreadSessionIds, action.sessionId]
          : state.unreadSessionIds,
      };
    }

    case 'APPEND_STREAMING_TEXT': {
      const msgs = [...(state.messagesBySession[action.sessionId] || [])];
      const last = msgs[msgs.length - 1];
      let newBubble = false;
      if (last && last.role === 'assistant') {
        // Mark running activities as completed when real text content arrives
        const activities = last.activities?.some((a) => a.status === 'running')
          ? last.activities.map((a) => (a.status === 'running' ? { ...a, status: 'completed' as const } : a))
          : last.activities;
        // Update contentBlocks: append to last text block or create new one
        const blocks = last.contentBlocks ? [...last.contentBlocks] : [];
        const lastBlock = blocks[blocks.length - 1];
        if (lastBlock && lastBlock.type === 'text') {
          blocks[blocks.length - 1] = { type: 'text', text: lastBlock.text + action.text };
        } else {
          blocks.push({ type: 'text', text: action.text });
        }
        msgs[msgs.length - 1] = { ...last, content: last.content + action.text, activities, contentBlocks: blocks };
      } else {
        newBubble = true;
        const newMsg: IChatMessage = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: action.text,
          contentBlocks: [{ type: 'text', text: action.text }],
          timestamp: Date.now(),
        };
        msgs.push(newMsg);
      }
      const tabId = `session:${action.sessionId}`;
      const shouldMarkUnread =
        newBubble
        && state.activeTabId !== tabId
        && !state.unreadSessionIds.includes(action.sessionId);
      return {
        ...state,
        messagesBySession: {
          ...state.messagesBySession,
          [action.sessionId]: msgs,
        },
        unreadSessionIds: shouldMarkUnread
          ? [...state.unreadSessionIds, action.sessionId]
          : state.unreadSessionIds,
      };
    }

    case 'SET_STREAMING':
      return { ...state, streamingSessionId: action.sessionId };

    case 'SET_FILE_TREE':
      return { ...state, fileTree: asArray(action.fileTree), loadingFiles: false };

    case 'SET_FILE_CONTENT':
      return {
        ...state,
        fileContents: { ...state.fileContents, [action.path]: action.content },
      };

    case 'CLEAR_FILE_CONTENT': {
      const { [action.path]: _, ...rest } = state.fileContents;
      return { ...state, fileContents: rest };
    }

    case 'SET_LOADING_PROJECTS':
      return { ...state, loadingProjects: action.loading };

    case 'SET_LOADING_SESSIONS':
      return { ...state, loadingSessions: action.loading };

    case 'SET_LOADING_FILES':
      return { ...state, loadingFiles: action.loading };

    case 'SET_MESSAGES':
      return {
        ...state,
        messagesBySession: {
          ...state.messagesBySession,
          [action.sessionId]: asArray(action.messages),
        },
      };

    case 'ADD_TOOL_ACTIVITY': {
      const msgs = [...(state.messagesBySession[action.sessionId] || [])];
      const last = msgs[msgs.length - 1];
      if (last && last.role === 'assistant') {
        const activities = last.activities ? [...last.activities] : [];
        if (!activities.some((a) => a.toolUseId === action.activity.toolUseId)) {
          activities.push(action.activity);
        }
        const blocks = last.contentBlocks ? [...last.contentBlocks] : [];
        if (!blocks.some((b) => b.type === 'activity' && b.toolUseId === action.activity.toolUseId)) {
          blocks.push({ type: 'activity', toolUseId: action.activity.toolUseId });
        }
        msgs[msgs.length - 1] = { ...last, activities, contentBlocks: blocks };
      } else {
        msgs.push({
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: '',
          activities: [action.activity],
          contentBlocks: [{ type: 'activity', toolUseId: action.activity.toolUseId }],
          timestamp: Date.now(),
        });
      }
      return {
        ...state,
        messagesBySession: { ...state.messagesBySession, [action.sessionId]: msgs },
      };
    }

    case 'UPDATE_TOOL_SUMMARY': {
      const msgs = [...(state.messagesBySession[action.sessionId] || [])];
      const last = msgs[msgs.length - 1];
      if (last && last.role === 'assistant' && last.activities) {
        const activities = last.activities.map((a) => {
          if (action.toolUseIds.indexOf(a.toolUseId) !== -1) {
            return { ...a, status: 'completed' as const, summary: action.summary };
          }
          return a;
        });
        msgs[msgs.length - 1] = { ...last, activities };
      }
      return {
        ...state,
        messagesBySession: { ...state.messagesBySession, [action.sessionId]: msgs },
      };
    }

    case 'UPDATE_TOOL_PROGRESS': {
      const msgs = [...(state.messagesBySession[action.sessionId] || [])];
      const last = msgs[msgs.length - 1];
      if (last && last.role === 'assistant' && last.activities) {
        const activities = last.activities.map((a) => {
          if (a.toolUseId === action.toolUseId) {
            return { ...a, elapsedSeconds: action.elapsedSeconds };
          }
          return a;
        });
        msgs[msgs.length - 1] = { ...last, activities };
      }
      return {
        ...state,
        messagesBySession: { ...state.messagesBySession, [action.sessionId]: msgs },
      };
    }

    case 'AGENT_FILES_CHANGED': {
      // Preserve cache for files currently open in tabs so the viewer keeps showing
      // what the user was reading until they click Reload. Drop the rest so reopening
      // them fetches fresh content.
      const openPaths = new Set(
        state.tabs
          .filter((t) => t.type === 'file' && t.filePath)
          .map((t) => t.filePath as string),
      );
      const preserved: Record<string, string> = {};
      Object.keys(state.fileContents).forEach((p) => {
        if (openPaths.has(p)) preserved[p] = state.fileContents[p];
      });
      return { ...state, agentFileChangeSeq: state.agentFileChangeSeq + 1, fileContents: preserved };
    }

    case 'RENAME_FILE_PATH': {
      const newTabs = state.tabs.map((t) => {
        if (t.type === 'file' && t.filePath === action.oldPath) {
          return { ...t, id: `file:${action.newPath}`, label: action.newLabel, filePath: action.newPath };
        }
        if (t.type === 'file' && t.filePath && t.filePath.startsWith(action.oldPath + '/')) {
          const newFilePath = action.newPath + t.filePath.substring(action.oldPath.length);
          return { ...t, id: `file:${newFilePath}`, filePath: newFilePath };
        }
        return t;
      });
      let newActiveTabId = state.activeTabId;
      if (newActiveTabId && newActiveTabId.startsWith(`file:${action.oldPath}`)) {
        newActiveTabId = `file:${action.newPath}${newActiveTabId.substring(`file:${action.oldPath}`.length)}`;
      }
      const newFileContents: Record<string, string> = {};
      Object.keys(state.fileContents).forEach((key) => {
        if (key === action.oldPath) {
          newFileContents[action.newPath] = state.fileContents[key];
        } else if (key.startsWith(action.oldPath + '/')) {
          newFileContents[action.newPath + key.substring(action.oldPath.length)] = state.fileContents[key];
        } else {
          newFileContents[key] = state.fileContents[key];
        }
      });
      return { ...state, tabs: newTabs, activeTabId: newActiveTabId, fileContents: newFileContents };
    }

    case 'INIT_FAVORITE_SESSIONS':
      return { ...state, favoriteSessionsByProject: action.favorites };

    case 'TOGGLE_FAVORITE_SESSION': {
      const current = state.favoriteSessionsByProject[action.projectId] ?? [];
      const isFav = current.includes(action.sessionId);
      const updated = isFav ? current.filter((id) => id !== action.sessionId) : [...current, action.sessionId];
      const newFavs = { ...state.favoriteSessionsByProject, [action.projectId]: updated };
      if (updated.length === 0) delete newFavs[action.projectId];
      saveFavoriteSessions(newFavs);
      return { ...state, favoriteSessionsByProject: newFavs };
    }

    default:
      return state;
  }
}

interface IAiAssistantContextValue {
  state: IAiAssistantState;
  dispatch: React.Dispatch<IAiAssistantAction>;
}

const AiAssistantContext = createContext<IAiAssistantContextValue>({
  state: initialState,
  dispatch: () => {},
});

export const AiAssistantProvider: React.FC = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    dispatch({ type: 'INIT_FAVORITE_SESSIONS', favorites: loadFavoriteSessions() });
  }, []);

  return <AiAssistantContext.Provider value={{ state, dispatch }}>{children}</AiAssistantContext.Provider>;
};

export const useAiAssistant = () => useContext(AiAssistantContext);

export default AiAssistantContext;
